import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-[#d32f2f] hover:text-white',
        outline:
          'border border-input bg-background hover:bg-[#2197f1] hover:text-white hover:border-[#2197f1]',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-[#2197f1] hover:text-white',
        ghost: 'hover:bg-[#2197f1] hover:text-white',
        link: 'text-primary underline-offset-4 hover:underline',
        // Design-system CTA styles — codify the hand-rolled className pattern used
        // across the app (ServiceFormModal, ConfirmModal, AiDetailPanel, DashboardPage)
        // so it can be reused instead of copy-pasted. Additive only: existing variants
        // above are untouched, so other pages keep their current look.
        brand:
          'rounded-[16px] bg-[var(--main-brand,#2197f1)] text-[color:var(--main-brand-foreground,#ffffff)] font-segoe font-semibold hover:bg-[#1a7acc]',
        // Neutral fill, not a translucent brand tint: across the app
        // rgba(33,151,241,0.2) marks a DISABLED control (MessageInputArea,
        // ArtifactSelectorDialog), so using it for live toolbar actions made the
        // whole toolbar read as greyed out.
        'brand-soft':
          'rounded-[16px] bg-[var(--auth-background,#f4f4f3)] text-[color:var(--black-text,#0d0d0d)] font-segoe font-semibold hover:bg-[var(--grey-accent,#eeeeee)]',
        'brand-outline':
          'rounded-[16px] border border-[var(--grey-accent,#eeeeee)] bg-transparent text-[color:var(--black-text,#0d0d0d)] font-segoe font-semibold hover:bg-[var(--main-brand,#2197f1)] hover:text-[color:var(--main-brand-foreground,#ffffff)] hover:border-[var(--main-brand,#2197f1)]',
        'brand-danger':
          'rounded-[16px] bg-[var(--error-soft,#ffeded)] text-[color:var(--error-strong,#b3261e)] font-segoe font-semibold hover:bg-[var(--error-strong,#b3261e)] hover:text-[color:var(--white,#ffffff)]',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
        // Paired with the brand* variants above: h-12/16px text for primary CTAs,
        // h-10/14px for the smaller toolbar and list-card actions.
        brand: 'h-[48px] px-[24px] py-[12px] text-[16px]',
        'brand-sm': 'h-10 px-[16px] py-[8px] text-[14px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
